import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Award, Users, FileCheck, HandCoins } from "lucide-react";
import { TESTIDS } from "@/constants/testIds";

const STATS = [
  { value: 20, suffix: "+", label: "Years of experience" },
  { value: 800, suffix: "+", label: "Projects completed" },
  { value: 100, suffix: "%", label: "Licensed & insured jobs" },
  { value: 5, suffix: "★", label: "Neighbor rated" },
];

const POINTS = [
  { icon: ShieldCheck, title: "Fully Licensed & Insured", desc: "Peace of mind on every job — full coverage, always." },
  { icon: Award, title: "Two Decades in the Trade", desc: "Long Island homes, from Old Westbury to Montauk." },
  { icon: Users, title: "Family Owned & Operated", desc: "Direct line to the owner — no call centers." },
  { icon: FileCheck, title: "Workmanship Warranty", desc: "We stand behind every board, tile and shingle." },
  { icon: HandCoins, title: "Free On-Site Estimates", desc: "Transparent pricing before a nail is driven." },
];

function Counter({ target, suffix }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let started = false;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            started = true;
            const duration = 1400;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setN(Math.round(target * eased));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 },
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [target]);
  return (
    <span ref={ref} className="font-mono-accent">
      {n}
      {suffix}
    </span>
  );
}

export default function WhyChoose() {
  return (
    <section
      id="about"
      className="relative bg-[color:var(--vs-bg)] py-24 md:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 blueprint-grid-lg opacity-60 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[color:var(--vs-blue)]" />
              <span className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[color:var(--vs-blue)]">
                003 / Why Vasquez
              </span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight">
              A promise built on
              <br />
              <span className="chrome-text">craftsmanship</span> &{" "}
              <span className="text-[color:var(--vs-blue)]">care.</span>
            </h2>
            <p className="mt-6 text-gray-400 text-base md:text-lg leading-relaxed max-w-xl">
              We treat every house like it&apos;s our own. Vasquez & Son is a
              father-and-son crew turning neighbor referrals into a two-decade
              track record.
            </p>

            <div className="mt-10 flex flex-col divide-y divide-white/10">
              {POINTS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="py-5 flex gap-5 items-start">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center border-2 border-[color:var(--vs-blue)]/50">
                    <Icon className="w-5 h-5 text-[color:var(--vs-blue)]" strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="font-display uppercase tracking-tight text-lg text-white font-semibold">
                      {title}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 lg:col-start-8">
            <div className="sticky top-28 grid grid-cols-2 gap-3">
              {STATS.map((s) => (
                <div
                  key={s.label}
                  className="border border-white/10 bg-[#111214] p-6 md:p-8 min-h-[170px] flex flex-col justify-between hover:border-[color:var(--vs-blue)]/60 transition-colors"
                >
                  <div className="text-4xl md:text-5xl font-bold chrome-text">
                    <Counter target={s.value} suffix={s.suffix} />
                  </div>
                  <div className="font-mono-accent text-[11px] tracking-[0.25em] uppercase text-gray-400 leading-relaxed">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
